import React, { useContext } from 'react'
// Icon to show-up when the widget is loading its data
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleXmark, faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import LoadingContext from 'context/LoadingContext'
import { useTranslation } from 'react-i18next'

import {
  Breadcrumb,
  BreadcrumbItem,
  IconButton,
  Inline,
  ProgressBar,
  Typography,
  useColorMode,
} from '@airbus/components-react/components'
import { logoHeight } from '@airbus/elements/elements'
import { pxToRem } from '@airbus/elements/helpers'
import LightDarkContext from 'context/LightDarkModeContext'
import { NavItem } from 'types/navItems'
import { NavLink, useNavigate } from 'react-router-dom'

type Props = {
  pageTitle: string | React.ReactNode
  modalEmbedded?: boolean
  modalOnClose?: () => void
  navItems?: NavItem[]
  ActionButtons?: React.FC<{ isLoading: boolean }>
  style?: React.CSSProperties
  localLoading?: boolean
}

const PageHeader = ({
  pageTitle,
  modalEmbedded = false,
  modalOnClose = () => {},
  ActionButtons,
  navItems,
  style,
  localLoading = false,
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  /* TODO: actionsButton context (for each page) to be used later on.
   * For now, just inserting any context (WO-toolbar context here) forces the above toolbarActions to be propagated
   */

  const { isLoading } = useContext(LoadingContext)
  const theme = useColorMode()
  const { getBackgroundColor } = useContext(LightDarkContext)

  const renderNavItem = (navItem: NavItem) => {
    return (
      <>
        <Typography variant='small'>{navItem.title}</Typography>
      </>
    )
  }

  return (
    <div
      style={{
        position: 'sticky',
        top: modalEmbedded ? '0' : `calc(${pxToRem(logoHeight)} + ${theme.size.spacing[16]}*2)`,
        backgroundColor: getBackgroundColor(),
        zIndex: theme.elevation.zindex.header,
        ...style,
      }}
    >
      {(isLoading || localLoading) && (
        <ProgressBar indeterminate style={{ position: 'absolute', top: 0, left: 0, width: '100%' }} />
      )}
      {!modalEmbedded && (
        <Inline style={{ padding: '10px' }} align='space-between'>
          <Breadcrumb aria-label='breadcrumb'>
            <BreadcrumbItem>
              <IconButton variant='primary' size='xsmall' onClick={() => navigate(-1)} title={t('pagination_previous')}>
                <FontAwesomeIcon icon={faArrowLeft} />
              </IconButton>
            </BreadcrumbItem>

            {navItems &&
              navItems.map((it, ind1) => (
                <BreadcrumbItem key={it.title}>
                  {ind1 < navItems.length - 1 ? (
                    <NavLink style={{ whiteSpace: 'pre' }} to={it.url}>
                      {renderNavItem(it)}
                    </NavLink>
                  ) : (
                    <span style={{ whiteSpace: 'pre' }}>{renderNavItem(it)}</span>
                  )}
                </BreadcrumbItem>
              ))}
          </Breadcrumb>
        </Inline>
      )}
      <Inline style={{ padding: '10px' }} align='space-between'>
        <Inline spacing='2-x'>
          <Typography variant='large'>{pageTitle}</Typography>
        </Inline>
        <Inline spacing='4-x'>
          {ActionButtons && <ActionButtons isLoading={isLoading || localLoading} />}
          {modalEmbedded && (
            <IconButton variant='ghost' size='xsmall' onClick={modalOnClose}>
              <FontAwesomeIcon icon={faCircleXmark} />
            </IconButton>
          )}
        </Inline>
      </Inline>
    </div>
  )
}

export default PageHeader
